import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AUDIT_ACTIONS, type AuditAction } from '@geoic/shared';
import { api, type ListResult } from '@/lib/api-client.js';
import { dateTime } from '@/lib/format.js';
import { queryKeys } from '@/lib/query-keys.js';
import { PageHeader } from '@/components/ui/Card.js';
import { Badge, type BadgeTone } from '@/components/ui/Badge.js';
import { DataTable, type Column } from '@/components/ui/DataTable.js';
import { Input, Select } from '@/components/ui/Field.js';
import { collectionLabel } from '@/features/media/media-api.js';

type AuditLog = {
  _id: string;
  userEmail: string;
  action: AuditAction;
  entityType: string;
  entityId: string | null;
  ip: string;
  createdAt: string;
};

const ACTION_LABELS: Record<AuditAction, { label: string; tone: BadgeTone }> = {
  create: { label: 'Yaratildi', tone: 'success' },
  update: { label: 'O‘zgartirildi', tone: 'info' },
  delete: { label: 'O‘chirildi', tone: 'danger' },
  publish: { label: 'Nashr qilindi', tone: 'success' },
  unpublish: { label: 'Nashrdan olindi', tone: 'neutral' },
  login: { label: 'Kirish', tone: 'neutral' },
  login_failed: { label: 'Kirish rad etildi', tone: 'warning' },
  settings_change: { label: 'Sozlama', tone: 'info' },
  price_change: { label: 'Narx o‘zgardi', tone: 'warning' },
  export: { label: 'Eksport', tone: 'neutral' },
  migration: { label: 'Migratsiya', tone: 'neutral' },
};

const ACTION_OPTIONS = AUDIT_ACTIONS.map((action) => ({
  value: action,
  label: ACTION_LABELS[action].label,
}));

/**
 * Amallar jurnali (TZ 3.16).
 *
 * Yozuvlar 365 kundan keyin avtomatik o'chadi (TTL indeks) — bu yerda
 * o'chirish tugmasi ataylab yo'q: jurnalni tahrirlash imkoni uni
 * ma'nosiz qilardi.
 */
export function AuditPage() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<AuditAction | ''>('');
  const [entityType, setEntityType] = useState('');

  const params = {
    page,
    limit: 50,
    action: action || undefined,
    entityType: entityType || undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.audit.list(params),
    queryFn: () => api.get<ListResult<AuditLog>>('/audit', params),
  });

  const columns: Column<AuditLog>[] = [
    {
      key: 'date',
      header: 'Sana',
      width: '160px',
      render: (log) => <span className="tabular text-n-600">{dateTime(log.createdAt)}</span>,
    },
    {
      key: 'user',
      header: 'Kim',
      render: (log) => <span className="text-n-800">{log.userEmail || '—'}</span>,
    },
    {
      key: 'action',
      header: 'Amal',
      render: (log) => {
        const config = ACTION_LABELS[log.action];
        return <Badge tone={config.tone}>{config.label}</Badge>;
      },
    },
    {
      key: 'entity',
      header: 'Bo‘lim',
      render: (log) => <span className="text-n-800">{collectionLabel(log.entityType)}</span>,
    },
    {
      key: 'ip',
      header: 'IP',
      hideOnTablet: true,
      render: (log) => <span className="font-mono text-xs text-n-600">{log.ip || '—'}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        title="Jurnal"
        description="Tizimdagi barcha o‘zgarishlar. Yozuvlar bir yildan keyin avtomatik o‘chadi."
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Select
            label="Amal"
            placeholder="Hammasi"
            options={ACTION_OPTIONS}
            value={action}
            onChange={(event) => {
              setAction(event.target.value as AuditAction | '');
              setPage(1);
            }}
          />
        </div>
        <div className="w-56">
          <Input
            label="Bo‘lim"
            value={entityType}
            onChange={(event) => {
              setEntityType(event.target.value);
              setPage(1);
            }}
            placeholder="products, news…"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(log) => log._id}
        loading={isLoading}
        meta={data?.meta}
        onPageChange={setPage}
        emptyTitle="Jurnalda yozuv yo‘q"
        emptyHint="Tizimda o‘zgarish qilinganda yozuvlar shu yerda paydo bo‘ladi."
      />
    </>
  );
}
